import { SEARCHER_OPTIONS, SEARCHER_TYPES } from '@core/constants/SEARCHER';
import { getStoreFilterStateById } from './store';
import { TRIGGER_SEARCH_QUERY_EVENT } from '@core/constants/EVENTS';
import { SEARCH_IMG_QUERY_PARAMS } from '@core/constants/API';
import { SWITCH_AI_DEFAULT } from '@core/constants/DEFAULT';

/**
 * Get the current searcher object.
 * @returns {object|undefined} - The current searcher object.
 */
export const getCurrentSearcherObj = () => {
    const /** @type {URLSearchParams} */ urlParams = new URLSearchParams(
            window.location.search,
        );

    if (!urlParams.has(SEARCHER_TYPES.SEARCHER_ID)) return;

    const /** @type {object|undefined} */ searcherObj = getStoreFilterStateById(
            urlParams.get(SEARCHER_TYPES.SEARCHER_ID),
        );

    if (!searcherObj) return;

    return searcherObj;
};

/**
 * Check if the current query is an image search query.
 * @param {URLSearchParams} urlParams - URL params to check if the current query is an image search query.
 * @param {object} currentQueryWithImages - Object with the current query with images.
 * @returns {boolean} - True if the current query is an image search query, otherwise false.
 */
export const isImgSearchQuery = (urlParams, currentQueryWithImages) => {
    return (
        urlParams.has(SEARCHER_TYPES.IMAGE_ID) &&
        currentQueryWithImages &&
        currentQueryWithImages[SEARCHER_TYPES.IMAGE_URLS] &&
        !urlParams.has(SEARCHER_TYPES.IMG_UID)
    );
};

/**
 * Dispatch the detail object to trigger the search query event
 * @param {object} detailObj - The detail object to dispatch
 * @param {boolean} [cancelable=false] - The event cancelable option
 * @returns {void}
 */
export const dispatchDetailIbj = (detailObj, cancelable = false) => {
    document.dispatchEvent(
        new CustomEvent(TRIGGER_SEARCH_QUERY_EVENT, {
            detail: detailObj,
            cancelable,
        }),
    );
};

/**
 * Get the collection to search.
 * @param {URLSearchParams} urlParams - The url params.
 * @returns {string} - The collection to search.
 */
export const getCollection = (urlParams) => {
    if (urlParams.has(SEARCHER_TYPES.OPTION)) {
        return urlParams
            .get(SEARCHER_TYPES.OPTION)
            .toLowerCase()
            .includes(SEARCHER_OPTIONS.ED_CODE)
            ? SEARCH_IMG_QUERY_PARAMS.EDITORIAL
            : SEARCH_IMG_QUERY_PARAMS.CREATIVE;
    }

    return SEARCH_IMG_QUERY_PARAMS.EDITORIAL;
};

/**
 * Get the collection for no AI.
 * @param {URLSearchParams} urlParams - The url params.
 * @returns {string} - The collection for no AI.
 */
export const getCollectionForNoAI = (urlParams) => {
    if (urlParams.has(SEARCHER_TYPES.OPTION)) {
        return urlParams
            .get(SEARCHER_TYPES.OPTION)
            .toLowerCase()
            .includes(SEARCHER_OPTIONS.CR_CODE)
            ? 'CREATIVO'
            : 'EDITORIAL';
    }

    return 'EDITORIAL';
};

/**
 * Get the search option URL.
 * @param {URLSearchParams} urlParams - The URL params.
 * @returns {string} - The search option URL.
 */
export const getSearchOptionUrl = (urlParams) => {
    return urlParams.get(SEARCHER_TYPES.OPTION) &&
        urlParams.get(SEARCHER_TYPES.OPTION) !== 'null'
        ? urlParams.get(SEARCHER_TYPES.OPTION)
        : SEARCHER_OPTIONS.ED_IMGS;
};

/**
 * Get the AI from the URL.
 * @param {URLSearchParams} urlParams - The URL params.
 * @returns {string} - The AI from the URL.
 */
export const getAiFromUrl = (urlParams) => {
    return urlParams.get(SEARCHER_TYPES.AI) && urlParams.get(SEARCHER_TYPES.AI) !== 'null'
        ? urlParams.get(SEARCHER_TYPES.AI)
        : SWITCH_AI_DEFAULT;
};

/**
 * Check if the url has the searcher option text.
 * @returns {boolean} - The url has the searcher option text.
 */
export const hasSearcherOptionText = () => {
    const /** @type {URLSearchParams} */ urlParams = new URLSearchParams(
            window.location.search,
        );

    return (
        urlParams.has(SEARCHER_TYPES.TEXT) && urlParams.get(SEARCHER_TYPES.TEXT).length
    );
};

/**
 * Allow the trigger search query event when:
 *   - The text is empty.
 *   - The urlParams has keywords or categories.
 *   - The urlParams has image id.
 *   - The urlParams has img uid.
 * @returns {boolean} - The trigger search query event.
 */
export const allowTriggerSearchQueryEvent = () => {
    const /** @type {URLSearchParams} */ urlParams = new URLSearchParams(
            window.location.search,
        );
    const /** @type {object|undefined} */ currentFiltersStateHistory =
            getCurrentSearcherObj();
    let /** @type {string[]} */ activatedFilters = [];
    let /** @type {string[]} */ activatedVideoFilters = [];

    if (currentFiltersStateHistory) {
        activatedFilters = currentFiltersStateHistory.activatedFilters;
        activatedVideoFilters = currentFiltersStateHistory.activatedVideoFilters;
    }

    return (
        hasSearcherOptionText() ||
        urlParams.has(SEARCHER_TYPES.KEYWORDS) ||
        urlParams.has(SEARCHER_TYPES.CATEGORIES) ||
        urlParams.has(SEARCHER_TYPES.IMAGE_ID) ||
        urlParams.has(SEARCHER_TYPES.IMG_UID) ||
        [...activatedFilters, ...activatedVideoFilters].length >= 2
    );
};

/**
 * Check if the AI is activated.
 * @returns {boolean} - The AI is activated.
 */
export const isAIActivated = () => {
    const /** @type {URLSearchParams} */ urlParams = new URLSearchParams(
            window.location.search,
        );

    return (
        urlParams.has(SEARCHER_TYPES.AI) && urlParams.get(SEARCHER_TYPES.AI) === 'true'
    );
};

/**
 * Get the current search option type.
 * @returns {string} - The current search option type.
 */
export const getCurrentSearchOptionType = () => {
    /** @type {URLSearchParams} */
    const urlParams = new URLSearchParams(window.location.search);

    return getSearchOptionUrl(urlParams);
};
